import React, { useState } from 'react';
import { 
  Menu, X, CheckCircle, Clock, Heart, MessageSquare, 
  Mail, User, Building, Send, Target
} from 'lucide-react';

const LEAD_SOURCES = ['Psychology Today', 'Web Contact Form'];
const RESPONSE_TONES = [
  'Warm and Empathetic',
  'Professional and Formal',
  'Direct and Informative',
  'Casual and Friendly',
];
const emptySignupForm = {
  practiceName: '',
  contactName: '',
  contactEmail: '',
  adminEmail: '',
  leadSources: [],
  psychologyTodayUrl: '',
  numClinicians: '',
  responseTone: '',
};
const emptySignupErrors = {
  practiceName: '',
  contactName: '',
  contactEmail: '',
  adminEmail: '',
  leadSources: '',
  psychologyTodayUrl: '',
  numClinicians: '',
  responseTone: '',
};

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [signupForm, setSignupForm] = useState(emptySignupForm);
  const [signupErrors, setSignupErrors] = useState(emptySignupErrors);
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const [signupSubmitted, setSignupSubmitted] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    practice: '',
    message: '',
  });
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    message: ''
  });

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };


  // Validation functions
  const validateName = (name) => {
    if (!name || name.trim().length < 2) {
      return 'Name must be at least 2 characters';
    }
    if (name.trim().length > 100) {
      return 'Name must be less than 100 characters';
    }
    const nameRegex = /^[a-zA-Z\s'-]+$/;
    if (!nameRegex.test(name.trim())) {
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return '';
  };

  const validateEmail = (email) => {
    if (!email) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validateMessage = (message) => {
    if (!message || message.trim().length < 10) {
      return 'Message must be at least 10 characters';
    }
    if (message.trim().length > 5000) {
      return 'Message must be less than 5000 characters';
    }
    return '';
  };

  // Handle field changes with validation
  const handleFieldChange = (field, value) => {
    setContactForm({...contactForm, [field]: value});
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors({...formErrors, [field]: ''});
    }
  };

  const openSignupModal = () => {
    setSignupForm(emptySignupForm);
    setSignupErrors(emptySignupErrors);
    setSignupSubmitted(false);
    setSignupModalOpen(true);
  };

  const setSignupField = (field, value) => {
    setSignupForm(f => ({ ...f, [field]: value }));
    if (signupErrors[field]) setSignupErrors(e => ({ ...e, [field]: '' }));
  };

  const toggleSignupLeadSource = (source) => {
    setSignupForm(f => {
      const next = f.leadSources.includes(source)
        ? f.leadSources.filter(s => s !== source)
        : [...f.leadSources, source];
      return { ...f, leadSources: next };
    });
    if (signupErrors.leadSources) setSignupErrors(e => ({ ...e, leadSources: '' }));
  };

  const validateSignup = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^[a-zA-Z\s'.-]{2,100}$/;
    const next = { ...emptySignupErrors };
    let valid = true;

    if (!signupForm.practiceName.trim()) { next.practiceName = 'Practice name is required'; valid = false; }
    if (!nameRegex.test(signupForm.contactName.trim())) { next.contactName = 'Please enter a valid contact name'; valid = false; }
    if (!emailRegex.test(signupForm.contactEmail.trim())) { next.contactEmail = 'Please enter a valid email address'; valid = false; }
    if (!emailRegex.test(signupForm.adminEmail.trim())) { next.adminEmail = 'Please enter a valid email address'; valid = false; }
    if (signupForm.leadSources.length === 0) { next.leadSources = 'Please select at least one lead source'; valid = false; }
    if (signupForm.leadSources.includes('Psychology Today')) {
      const ptUrl = signupForm.psychologyTodayUrl.trim();
      if (!ptUrl) {
        next.psychologyTodayUrl = 'Psychology Today profile URL is required'; valid = false;
      } else if (!ptUrl.startsWith('https://www.psychologytoday.com/')) {
        next.psychologyTodayUrl = 'Please enter a valid Psychology Today profile URL'; valid = false;
      }
    }
    if (!signupForm.numClinicians || isNaN(signupForm.numClinicians) || Number(signupForm.numClinicians) < 1) {
      next.numClinicians = 'Please enter a valid number of clinicians'; valid = false;
    }
    if (!signupForm.responseTone) { next.responseTone = 'Please select a response tone'; valid = false; }

    setSignupErrors(next);
    return valid;
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    if (!validateSignup()) return;

    setSignupSubmitting(true);
    try {
      const response = await fetch('/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupForm),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Submission failed');
      }

      setSignupSubmitted(true);
      setSignupForm(emptySignupForm);
    } catch (err) {
      console.error('Signup error:', err);
      alert('Submission failed. Please try again or email us at info@intakehelp.ai');
    } finally {
      setSignupSubmitting(false);
    }
  };

  // Contact form handler with backend integration
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submitting
    const nameError = validateName(contactForm.name);
    const emailError = validateEmail(contactForm.email);
    const messageError = validateMessage(contactForm.message);
    
    if (nameError || emailError || messageError) {
      setFormErrors({
        name: nameError,
        email: emailError,
        message: messageError
      });
      return;
    }
    
    try {
      // Get reCAPTCHA token
      const token = await window.grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        { action: 'submit' }
      );

      // Send to backend
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...contactForm,
          recaptchaToken: token
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send message');
      }

      // Show success
      setContactSubmitted(true);
      setContactForm({ name: '', email: '', practice: '', message: '' });
      setFormErrors({ name: '', email: '', message: '' });
      setTimeout(() => setContactSubmitted(false), 5000);

    } catch (error) {
      console.error('Contact form error:', error);
      alert('Failed to send message. Please try again or email us directly at info@intakehelp.ai');
    }
  };

  const showPsychologyTodayUrl = signupForm.leadSources.includes('Psychology Today');

  return (
    <div className="min-h-screen bg-cloud-white">

      {/* ===== SIGNUP MODAL ===== */}
      {signupModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(44,62,80,0.6)' }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-8 pt-8 pb-4 border-b border-gray-100">
              <h2 className="text-2xl font-bold text-deep-slate">Sign Up</h2>
              <button
                onClick={() => setSignupModalOpen(false)}
                className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-deep-slate font-semibold px-4 py-2 rounded-lg transition-colors text-sm"
              >
                <X size={16} />
                Close
              </button>
            </div>

            <div className="px-8 py-6">
              {signupSubmitted ? (
                <div className="bg-healing-sage/20 border-2 border-healing-sage rounded-xl p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-healing-sage mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-deep-slate mb-2">Request Received!</h3>
                  <p className="text-neutral-gray text-lg">We'll be in touch shortly to get you set up.</p>
                </div>
              ) : (
                <form onSubmit={handleSignupSubmit} noValidate className="space-y-5">

                  {/* Practice Name */}
                  <div>
                    <label className="block text-deep-slate font-semibold mb-2">Practice Name *</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-3.5 w-5 h-5 text-neutral-gray" />
                      <input
                        type="text"
                        placeholder="Your practice name"
                        value={signupForm.practiceName}
                        onChange={e => setSignupField('practiceName', e.target.value)}
                        className={`w-full pl-11 pr-4 py-3 border-2 ${signupErrors.practiceName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:border-healing-sage focus:outline-none`}
                      />
                    </div>
                    {signupErrors.practiceName && <p className="text-red-500 text-sm mt-1">{signupErrors.practiceName}</p>}
                  </div>

                  {/* Contact Name */}
                  <div>
                    <label className="block text-deep-slate font-semibold mb-2">Contact Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-3.5 w-5 h-5 text-neutral-gray" />
                      <input
                        type="text"
                        placeholder="Your full name"
                        value={signupForm.contactName}
                        onChange={e => setSignupField('contactName', e.target.value)}
                        className={`w-full pl-11 pr-4 py-3 border-2 ${signupErrors.contactName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:border-healing-sage focus:outline-none`}
                      />
                    </div>
                    {signupErrors.contactName && <p className="text-red-500 text-sm mt-1">{signupErrors.contactName}</p>}
                  </div>

                  {/* Contact Email */}
                  <div>
                    <label className="block text-deep-slate font-semibold mb-2">Contact Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3.5 w-5 h-5 text-neutral-gray" />
                      <input
                        type="email"
                        placeholder="you@practice.com"
                        value={signupForm.contactEmail}
                        onChange={e => setSignupField('contactEmail', e.target.value)}
                        className={`w-full pl-11 pr-4 py-3 border-2 ${signupErrors.contactEmail ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:border-healing-sage focus:outline-none`}
                      />
                    </div>
                    {signupErrors.contactEmail && <p className="text-red-500 text-sm mt-1">{signupErrors.contactEmail}</p>}
                  </div>

                  {/* Admin Email */}
                  <div>
                    <label className="block text-deep-slate font-semibold mb-2">
                      Admin Email (the email address your leads are forwarded from) *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3.5 w-5 h-5 text-neutral-gray" />
                      <input
                        type="email"
                        placeholder="admin@practice.com"
                        value={signupForm.adminEmail}
                        onChange={e => setSignupField('adminEmail', e.target.value)}
                        className={`w-full pl-11 pr-4 py-3 border-2 ${signupErrors.adminEmail ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:border-healing-sage focus:outline-none`}
                      />
                    </div>
                    {signupErrors.adminEmail && <p className="text-red-500 text-sm mt-1">{signupErrors.adminEmail}</p>}
                  </div>

                  {/* Lead Sources */}
                  <div>
                    <label className="block text-deep-slate font-semibold mb-2">Lead Sources *</label>
                    <div className="space-y-2">
                      {LEAD_SOURCES.map(source => (
                        <label key={source} className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={signupForm.leadSources.includes(source)}
                            onChange={() => toggleSignupLeadSource(source)}
                            className="w-5 h-5 border-2 border-gray-300 rounded focus:ring-healing-sage focus:ring-2 cursor-pointer"
                            style={{ accentColor: '#7AB89D' }}
                          />
                          <span className="ml-3 text-deep-slate">{source}</span>
                        </label>
                      ))}
                    </div>
                    {signupErrors.leadSources && <p className="text-red-500 text-sm mt-1">{signupErrors.leadSources}</p>}
                  </div>

                  {/* Psychology Today URL (conditional) */}
                  {showPsychologyTodayUrl && (
                    <div>
                      <label className="block text-deep-slate font-semibold mb-1">Psychology Today Profile URL *</label>
                      <p className="text-sm text-gray-400 mb-2">Copy and paste the URL directly from your Psychology Today profile page.</p>
                      <input
                        type="text"
                        placeholder="https://www.psychologytoday.com/us/therapists/..."
                        value={signupForm.psychologyTodayUrl}
                        onChange={e => setSignupField('psychologyTodayUrl', e.target.value)}
                        className={`w-full px-4 py-3 border-2 ${signupErrors.psychologyTodayUrl ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:border-healing-sage focus:outline-none`}
                      />
                      {signupErrors.psychologyTodayUrl && <p className="text-red-500 text-sm mt-1">{signupErrors.psychologyTodayUrl}</p>}
                    </div>
                  )}

                  {/* Number of Clinicians */}
                  <div>
                    <label className="block text-deep-slate font-semibold mb-2">Number of Clinicians *</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="e.g. 3"
                      value={signupForm.numClinicians}
                      onChange={e => setSignupField('numClinicians', e.target.value)}
                      className={`w-full px-4 py-3 border-2 ${signupErrors.numClinicians ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:border-healing-sage focus:outline-none`}
                    />
                    {signupErrors.numClinicians && <p className="text-red-500 text-sm mt-1">{signupErrors.numClinicians}</p>}
                  </div>

                  {/* Desired Response Tone */}
                  <div>
                    <label className="block text-deep-slate font-semibold mb-2">Desired Response Tone *</label>
                    <select
                      value={signupForm.responseTone}
                      onChange={e => setSignupField('responseTone', e.target.value)}
                      className={`w-full px-4 py-3 border-2 ${signupErrors.responseTone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:border-healing-sage focus:outline-none bg-white text-deep-slate`}
                    >
                      <option value="">Select a tone...</option>
                      {RESPONSE_TONES.map(tone => (
                        <option key={tone} value={tone}>{tone}</option>
                      ))}
                    </select>
                    {signupErrors.responseTone && <p className="text-red-500 text-sm mt-1">{signupErrors.responseTone}</p>}
                  </div>

                  {/* Disclaimer */}
                  <p className="text-sm text-gray-400 italic">
                    Upon submission, we'll reach out with our standard terms of service and Business Associate Agreement (BAA) for your review and acceptance before proceeding.
                  </p>

                  <button
                    type="submit"
                    disabled={signupSubmitting}
                    className="w-full bg-amber-glow hover:bg-amber-glow-dark text-deep-slate font-bold py-4 px-8 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {signupSubmitting ? 'Submitting...' : 'Submit'}
                  </button>

                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== HEADER / NAVIGATION ===== */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/images/logo-full.png" 
                alt="IntakeHelp.ai" 
                className="h-10 md:h-12 lg:h-14 cursor-pointer"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setContactForm({ name: '', email: '', practice: '', message: '' });
                  setFormErrors({ name: '', email: '', message: '' });
                  setContactSubmitted(false);
                }}
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-3 lg:space-x-8">
              <button 
                onClick={() => scrollToSection('features')}
                className="text-deep-slate hover:text-healing-sage transition-colors font-medium text-sm lg:text-base"
              >
                Benefits
              </button>
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className="text-deep-slate hover:text-healing-sage transition-colors font-medium text-sm lg:text-base whitespace-nowrap"
              >
                How We Help
              </button>
              <button 
                onClick={() => scrollToSection('user-feedback')}
                className="text-deep-slate hover:text-healing-sage transition-colors font-medium text-sm lg:text-base whitespace-nowrap"
              >
                User Feedback
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-deep-slate hover:text-healing-sage transition-colors font-medium text-sm lg:text-base"
              >
                Contact
              </button>
              <button
                onClick={openSignupModal}
                className="bg-amber-glow hover:bg-amber-glow-dark text-deep-slate font-semibold px-4 py-2 lg:px-6 lg:py-2 rounded-lg transition-all shadow-md hover:shadow-lg text-sm lg:text-base whitespace-nowrap"
              >
                Sign Up Now
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-deep-slate p-2"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col space-y-4">
                <button 
                  onClick={() => scrollToSection('features')}
                  className="text-deep-slate hover:text-healing-sage transition-colors font-medium text-left"
                >
                  Benefits
                </button>
                <button 
                  onClick={() => scrollToSection('how-it-works')}
                  className="text-deep-slate hover:text-healing-sage transition-colors font-medium text-left"
                >
                  How We Help
                </button>
                <button 
                  onClick={() => scrollToSection('user-feedback')}
                  className="text-deep-slate hover:text-healing-sage transition-colors font-medium text-left"
                >
                  User Feedback
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-deep-slate hover:text-healing-sage transition-colors font-medium text-left"
                >
                  Contact
                </button>
                <button
                  onClick={openSignupModal}
                  className="bg-amber-glow hover:bg-amber-glow-dark text-deep-slate font-semibold px-6 py-2 rounded-lg transition-all shadow-md hover:shadow-lg text-center"
                >
                  Sign Up Now
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Add spacing for fixed header */}
      <div className="h-20"></div>

      {/* ===== HERO SECTION ===== */}
      <section 
        className="relative text-white py-20 md:py-32 px-6 overflow-hidden"
        style={{
          backgroundImage: 'linear-gradient(rgba(122, 184, 157, 0.65), rgba(90, 138, 118, 0.65)), url(/images/IntakeHelpCounselingChairs.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-black mb-4 leading-tight drop-shadow-lg">
            Your AI Clinical Admin Assistant
          </h1>
          <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto leading-relaxed text-white/95 font-medium">
            Inbound email inquiries read.<br />
            Responses drafted instantly, waiting for your approval.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
            <button
              onClick={openSignupModal}
              className="bg-amber-glow hover:bg-amber-glow-dark text-deep-slate font-bold px-10 py-5 rounded-lg text-xl transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5"
            >
              Sign Up Now
            </button>
            <button 
              onClick={() => scrollToSection('how-it-works')}
              className="bg-white hover:bg-gray-50 text-deep-slate font-semibold px-10 py-5 rounded-lg text-xl transition-all border-2 border-healing-sage shadow-xl hover:shadow-2xl hover:-translate-y-0.5"
            >
              See How It Works
            </button>
          </div>
        </div>
      </section>

      {/* ===== VALUE PROPOSITIONS SECTION ===== */}
      <section id="features" className="py-16 px-6 bg-gray-50 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-deep-slate mb-4">
              Care Begins with Connection
            </h2>
            <p className="text-xl text-neutral-gray max-w-3xl mx-auto">
              We empower mental health practices to focus on what matters most - <span className="underline">Helping Clients</span> - by automating the complex administrative burden of intake processes.
            </p>
          </div>

          {/* Three Value Proposition Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Card 1: Empathetic Communication (MOVED TO FIRST) */}
            <div className="bg-white border-t-4 border-healing-sage rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all p-8 flex flex-col">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-healing-sage rounded-lg flex items-center justify-center">
                  <Heart className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-deep-slate mb-4 text-center">
                Empathetic Communication
              </h3>
              <p className="text-neutral-gray mb-4 text-center leading-relaxed flex-grow">
                Warm, understanding communication designed for the sensitive nature of mental health. 
                Technology that truly cares, never robotic.
              </p>
              <div className="border-t border-gray-200 pt-6 mt-auto">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-healing-sage mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-deep-slate">Maintains your unique voice</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-healing-sage mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-deep-slate">Recognizes client courage</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-healing-sage mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-deep-slate">Always respectful & warm</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Card 2: Prompt Response (NOW SECOND) */}
            <div className="bg-white border-t-4 border-healing-sage rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all p-8 flex flex-col">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-healing-sage rounded-lg flex items-center justify-center">
                  <Clock className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-deep-slate mb-4 text-center">
                Prompt Response
              </h3>
              <p className="text-neutral-gray mb-4 text-center leading-relaxed flex-grow">
                24/7 response to patient inquiries. Never miss an opportunity. 
                Instant support during clients' moment of motivation to seek help.
              </p>
              <div className="border-t border-gray-200 pt-6 mt-auto">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-healing-sage mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-deep-slate">Always available</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-healing-sage mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-deep-slate">No missed opportunities</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-healing-sage mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-deep-slate">Better client engagement</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Card 3: Quality Data Capture (NOW THIRD) */}
            <div className="bg-white border-t-4 border-healing-sage rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all p-8 flex flex-col">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-healing-sage rounded-lg flex items-center justify-center">
                  <Target className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-deep-slate mb-4 text-center">
                Quality Data Capture
              </h3>
              <p className="text-neutral-gray mb-4 text-center leading-relaxed flex-grow">
                Comprehensive information gathering on first contact. Get it right the first time 
                with structured data ready for your practice management system.
              </p>
              <div className="border-t border-gray-200 pt-6 mt-auto">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-healing-sage mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-deep-slate">Insurance verification guidance</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-healing-sage mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-deep-slate">Complete client information</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-healing-sage mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-deep-slate">Structured, organized data</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ===== HOW WE HELP SECTION ===== */}
      <section id="how-it-works" className="py-16 px-6 bg-light-blue scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-deep-slate mb-4">
              Your AI Admin Assistant
            </h2>
            <p className="text-xl text-neutral-gray max-w-3xl mx-auto leading-relaxed">
              Imagine opening your email between sessions. Instead of staring at a blank screen, 
              you have a compassionate, clinically accurate response ready to go. You get the speed 
              of AI, but keep the control of a human.
            </p>
          </div>

          {/* Horizontal Infographic - Desktop Only */}
          <div className="hidden lg:block mb-12">
            <img 
              src="/images/how-it-works-infographic.jpg" 
              alt="How IntakeHelp Works: Forward, Processing, Draft Handoff" 
              className="w-full max-w-5xl mx-auto rounded-xl shadow-2xl"
            />
          </div>

          {/* Vertical Infographic - Mobile/Tablet Only */}
          <div className="lg:hidden mb-12">
            <img 
              src="/images/how-it-works-infographic-vertical.jpg" 
              alt="How IntakeHelp Works: Forward, Processing, Draft Handoff" 
              className="w-full max-w-2xl mx-auto rounded-xl shadow-2xl"
            />
          </div>

          {/* Why Drafts + Supported Lead Services side by side */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">

            {/* Why Drafts Callout */}
            <div className="bg-gray-50 border-t-4 border-healing-sage rounded-lg shadow-xl transition-all p-8 h-full">
              <h4 className="text-2xl font-bold text-deep-slate mb-4">
                Why Drafts? Because Clinical Nuance Matters.
              </h4>
              <p className="text-neutral-gray text-lg leading-relaxed">
                By letting you review the email first, we ensure you never overcommit or accept a client
                that is not the right fit for you.
                <br />
                <strong className="text-deep-slate">95% Automation,
                5% Review, 100% Safe.</strong>
              </p>
            </div>

            {/* Supported Lead Services */}
            <div className="bg-white border-t-4 border-amber-glow rounded-xl shadow-xl p-8 h-full">
              <h4 className="text-2xl font-bold text-deep-slate mb-6 text-center">
                Supported Lead Services
              </h4>
              <ul className="space-y-4 text-deep-slate text-lg">
                <li className="flex items-start">
                  <span className="text-amber-glow mr-3 text-2xl font-bold">•</span>
                  <span>Psychology Today</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-glow mr-3 text-2xl font-bold">•</span>
                  <span>Website Contact Forms</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-glow mr-3 text-2xl font-bold">•</span>
                  <span>Additional lead sources being considered based on popularity</span>
                </li>
              </ul>
            </div>

          </div>
          <div className="max-w-4xl mx-auto mt-12">
            <h3 className="text-3xl font-bold text-deep-slate mb-6 text-center">See IntakeHelp in Action</h3>
            <div className="relative w-full rounded-xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/NctBJrbBv80"
                title="IntakeHelp Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ border: 0 }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF / TESTIMONIAL SECTION ===== */}
      <section id="user-feedback" className="py-16 px-6 bg-white scroll-mt-20">
        <div className="max-w-5xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-deep-slate mb-4">
              User Feedback
            </h2>
          </div>

          {/* Testimonial Cards Grid */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">

            {/* Rachel Collins */}
            <div className="bg-deep-slate text-white rounded-xl shadow-xl transition-all p-6 border-t-4 border-amber-glow text-center">
              <div className="flex items-center justify-center mb-4">
                <div className="w-20 h-20 rounded-full overflow-hidden shadow-lg bg-gray-200">
                  <img
                    src="/images/rachel-collins.jpg"
                    alt="Rachel Collins, LCPC"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div class="w-full h-full bg-healing-sage flex items-center justify-center text-white text-xl font-bold">
                          RC
                        </div>
                      `;
                    }}
                  />
                </div>
              </div>
              <blockquote>
                <p className="text-lg font-bold mb-3 leading-snug">
                  "I can't live without this tool anymore."
                </p>
                <p className="text-base mb-4 opacity-90 leading-relaxed">
                  I was so tired of copy-pasting the same emails. Now, IntakeHelp drafts everything for me.
                  I just check it and hit send. It creates responses quickly, accurately, and honestly—with
                  more empathy than I could provide by 4 PM on a Friday after a long week of seeing clients.
                </p>
                <footer className="text-base font-semibold">
                  <div className="mb-1">
                    <span className="font-bold">Rachel Collins, LCPC</span>
                  </div>
                  <div className="text-amber-glow">Founder, Stride Forward Counseling</div>
                </footer>
              </blockquote>
            </div>

            {/* Tatiana Royal-Williams */}
            <div className="bg-deep-slate text-white rounded-xl shadow-xl transition-all p-6 border-t-4 border-amber-glow text-center">
              <div className="flex items-center justify-center mb-4">
                <div className="w-20 h-20 rounded-full overflow-hidden shadow-lg bg-gray-200">
                  <img
                    src="/images/tatiana-royal-williams.png"
                    alt="Tatiana Royal-Williams, LCSW"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div class="w-full h-full bg-healing-sage flex items-center justify-center text-white text-xl font-bold">
                          TRW
                        </div>
                      `;
                    }}
                  />
                </div>
              </div>
              <blockquote>
                <p className="text-lg font-bold mb-3 leading-snug">
                  "The IntakeHelp AI Admin saves me time and helps us continue to serve our mission."
                </p>
                <p className="text-base mb-4 opacity-90 leading-relaxed">
                  As a group practice owner the IntakeHelp AI Admin has helped us streamline responses to
                  client inquiries, quickly and accurately providing potential clients with information about
                  our practice, clinicians, and their availability.
                </p>
                <footer className="text-base font-semibold">
                  <div className="mb-1">
                    <span className="font-bold">Tatiana Royal-Williams, LCSW</span>
                  </div>
                  <div className="text-amber-glow">Founder &amp; Clinical Director, With Heart With Soul Therapy</div>
                </footer>
              </blockquote>
            </div>

          </div>

          {/* Stats Bar */}
          <div className="mt-8 grid md:grid-cols-2 gap-6 text-center max-w-2xl mx-auto">
            <div className="bg-neutral-gray rounded-xl shadow-xl transition-all p-6">
              <div className="flex justify-center mb-3">
                <Mail className="w-10 h-10 text-white" />
              </div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">4,000+</div>
              <div className="text-lg text-white font-semibold">Inquiries Processed</div>
            </div>
            <div className="bg-neutral-gray rounded-xl shadow-xl transition-all p-6">
              <div className="flex justify-center mb-3">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">Hundreds</div>
              <div className="text-lg text-white font-semibold">of Hours Saved</div>
            </div>
          </div>

        </div>
      </section>

      {/* ===== CONTACT FORM SECTION ===== */}
      <section id="contact" className="py-16 px-6 bg-light-blue scroll-mt-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-deep-slate mb-4">
              Have Questions?
            </h2>
            <p className="text-xl text-neutral-gray">
              We're here to help you get back to what matters most - your clients.
            </p>
          </div>

          {/* Contact Form */}
          {!contactSubmitted ? (
            <form onSubmit={handleContactSubmit} className="bg-white border-t-4 border-healing-sage rounded-xl shadow-xl p-6">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-deep-slate font-semibold mb-2">
                    Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3.5 w-5 h-5 text-neutral-gray" />
                    <input
                      type="text"
                      placeholder="Your name"
                      value={contactForm.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      className={`w-full pl-11 pr-4 py-3 border-2 ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:border-healing-sage focus:outline-none`}
                    />
                  </div>
                  {formErrors.name && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-deep-slate font-semibold mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 w-5 h-5 text-neutral-gray" />
                    <input
                      type="email"
                      placeholder="your.email@practice.com"
                      value={contactForm.email}
                      onChange={(e) => handleFieldChange('email', e.target.value)}
                      className={`w-full pl-11 pr-4 py-3 border-2 ${formErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:border-healing-sage focus:outline-none`}
                    />
                  </div>
                  {formErrors.email && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-deep-slate font-semibold mb-2">
                  Practice Name
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-3.5 w-5 h-5 text-neutral-gray" />
                  <input
                    type="text"
                    placeholder="Your practice name (optional)"
                    value={contactForm.practice}
                    onChange={(e) => setContactForm({...contactForm, practice: e.target.value})}
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-healing-sage focus:outline-none"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-deep-slate font-semibold mb-2">
                  Message *
                </label>
                <textarea
                  placeholder="Tell us about your practice and how we can help..."
                  value={contactForm.message}
                  onChange={(e) => handleFieldChange('message', e.target.value)}
                  rows="5"
                  className={`w-full px-4 py-3 border-2 ${formErrors.message ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:border-healing-sage focus:outline-none resize-none`}
                ></textarea>
                {formErrors.message && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.message}</p>
                )}
              </div>

              <button
                type="submit"
                className="max-w-md mx-auto bg-amber-glow hover:bg-amber-glow-dark text-deep-slate font-bold py-4 px-8 rounded-lg text-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center"
              >
                <Send className="w-5 h-5 mr-2" />
                Send Message
              </button>
            </form>
          ) : (
            <div className="bg-healing-sage/20 border-2 border-healing-sage rounded-xl p-8 text-center">
              <div className="flex items-center justify-center mb-4">
                <CheckCircle className="w-12 h-12 text-healing-sage" />
              </div>
              <h3 className="text-2xl font-bold text-deep-slate mb-2">
                Message Sent Successfully!
              </h3>
              <p className="text-neutral-gray text-lg">
                We'll get back to you within 24 hours.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-deep-slate text-white py-6 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 items-center">
            
            {/* Logo - Left */}
            <div className="text-center md:text-left">
              <img 
                src="/images/logo-white.png" 
                alt="IntakeHelp.ai" 
                className="h-8 mx-auto md:mx-0 mb-3"
              />
              <p className="text-sm text-gray-400">
                Streamlining Mental Health<br />Practice Administration
              </p>
            </div>

            {/* Social Links - Center */}
            <div className="flex justify-center space-x-6">
              <a 
                href="https://www.linkedin.com/company/intakehelp/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-amber-glow transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61587425463010" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-amber-glow transition-colors"
                aria-label="Facebook"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/intakehelp.ai/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-amber-glow transition-colors"
                aria-label="Instagram"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a 
                href="https://bsky.app/profile/intakehelp.bsky.social" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-amber-glow transition-colors"
                aria-label="Bluesky"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z"/>
                </svg>
              </a>
              <a 
                href="https://x.com/intakehelp" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-amber-glow transition-colors"
                aria-label="Twitter/X"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a
                href="https://www.youtube.com/@intakehelp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-amber-glow transition-colors"
                aria-label="YouTube"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>

            {/* Copyright - Right */}
            <div className="text-center md:text-right">
              <p className="text-sm text-gray-400">
                © {new Date().getFullYear()} IntakeHelp<br />
                All rights reserved.
              </p>
            </div>

          </div>
        </div>
      </footer>

    </div>
  );
}

export default App;